import type {ColumnDef, SortingFn} from "@tanstack/react-table"
import {NavLink} from "react-router-dom"
import type {OtkBlank} from "shared"

import {Checkbox} from "@/components/ui/checkbox"
import {Status} from "@/components/Status"
import {blankStatusLabels, blankTypeLabels, formatUaDate, type WarehouseTabStatus,} from "@/lib/warehouse"

// warehouse.md's "Серія і номер" column — the column shows two fields, so
// sorting on `series` alone leaves numbers in arbitrary order inside a
// series; joins both, same as the wireframe's own custom `sortingFn`.
export const seriesSortingFn: SortingFn<OtkBlank> = (rowA, rowB) => {
  const a = `${rowA.original.series}${rowA.original.number}`
  const b = `${rowB.original.series}${rowB.original.number}`
  return a.localeCompare(b)
}

/**
 * warehouse.md's per-tab column set — ported from `buildColumns`. Two real
 * fields the page spec documents (`documentType`/`documentStatus`, "Тип
 * Документа"/"Статус Документа") have no backing field on the real
 * `OtkBlank` (`shared`'s schema only carries `documentId`/`documentNumber`/
 * `signedAt` for the linked document, not its own type/status) — omitted
 * here rather than faked; a real value needs the blanks endpoint to either
 * embed the linked document's summary or accept an expand param.
 */
export function buildWarehouseColumns({
  tab,
  onViewAct,
  canSelectForDestruction,
  returnTo,
}: {
  tab: WarehouseTabStatus
  onViewAct: (actId: string) => void
  /** Wireframe's `destructionTab && canDestroy` — the select column only exists on «Доступні»/«Анульовані», and only with the destroy permission. */
  canSelectForDestruction: boolean
  /** `NavLink`'s `state.returnTo` for the document link — the exact current URL (path + query), so closing the document modal comes back to this tab/filter, not a hand-built guess. */
  returnTo: string
}): ColumnDef<OtkBlank>[] {
  const columns: ColumnDef<OtkBlank>[] = []

  // warehouse.md: «Анульовані» is the one tab whose column set can grow to
  // select + series + type + documentNumber + signedAt + note + status —
  // measured (in the wireframe) to overflow the *page*, not just the
  // table, once every column also carries a rounded-up `min-w-*`. The fix
  // ported here is the same one: on that tab only, switch to `table-auto`
  // with **no minimum** on any fixed column (`w-px` lets auto-layout
  // shrink it to its content) and let "Примітка" (`w-full max-w-0`) absorb
  // whatever width is left over. Every other tab keeps `table-fixed` with
  // real pixel widths, since it never carries this many columns at once.
  const flexibleNote = tab === "annulled"
  const w = flexibleNote
    ? {
        select: "w-px",
        series: "w-px",
        type: "w-px",
        documentNumber: "w-px",
        signedAt: "w-px",
        status: "w-px",
        note: "w-full max-w-0 min-w-24",
      }
    : {
        select: "w-10",
        series: "w-40",
        type: "w-44",
        documentNumber: "w-44",
        signedAt: "w-36",
        status: "w-32",
        note: "w-48",
      }

  // Wireframe's select column: `DataTable` owns the selection *state*, but
  // has no shared checkbox-column primitive of its own — every consumer
  // that wants selection supplies the column itself. `row.getCanSelect()`
  // already reflects the page's `enableRowSelection` predicate, so an
  // ineligible row renders no checkbox at all, and "select all" can't
  // capture it either.
  if (canSelectForDestruction) {
    columns.push({
      id: "select",
      meta: { className: w.select },
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          aria-label="Вибрати всі придатні бланки на цій сторінці"
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? "indeterminate"
                : false
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        />
      ),
      cell: ({ row }) =>
        row.getCanSelect() ? (
          <Checkbox
            aria-label={`Вибрати бланк ${row.original.series} ${row.original.number}`}
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
          />
        ) : null,
    })
  }

  columns.push(
    {
      accessorKey: "series",
      header: "Серія і номер",
      meta: { className: w.series, truncate: true },
      sortingFn: seriesSortingFn,
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.series} {row.original.number}
        </span>
      ),
    },
    {
      accessorKey: "type",
      header: "Тип",
      meta: { className: w.type, truncate: true },
      cell: ({ row }) => blankTypeLabels[row.original.type],
    }
  )

  if (tab === "used" || tab === "annulled" || tab === "destroyed") {
    columns.push({
      id: "documentNumber",
      header: "Номер документа",
      enableSorting: false,
      meta: {
        className: tab === "destroyed" ? "w-44" : w.documentNumber,
        truncate: true,
      },
      cell: ({ row }) =>
        row.original.documentId ? (
          <NavLink
            className="font-medium text-primary hover:underline"
            state={{ returnTo }}
            to={`/documents/${row.original.documentId}`}
          >
            {row.original.documentNumber}
          </NavLink>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    })
  }

  if (tab === "used" || tab === "annulled") {
    columns.push({
      accessorKey: "signedAt",
      header: "Дата підпису",
      enableSorting: false,
      meta: { className: tab === "used" ? "w-36" : w.signedAt },
      cell: ({ row }) => {
        const formatted = formatUaDate(row.original.signedAt)
        return row.original.signedAt ? (
          formatted
        ) : (
          <span className="text-muted-foreground">{formatted}</span>
        )
      },
    })
  }

  if (tab === "destroyed") {
    columns.push({
      id: "destructionAct",
      header: "Акт Знищення",
      meta: { className: "w-40", truncate: true },
      enableSorting: false,
      cell: ({ row }) => {
        const { destructionActId, destructionActNumber } = row.original
        if (!destructionActId || !destructionActNumber) {
          return <span className="text-muted-foreground">—</span>
        }
        return (
          <button
            aria-label={`Переглянути акт знищення № ${destructionActNumber}`}
            className="max-w-full truncate align-bottom font-medium text-primary hover:underline"
            onClick={() => onViewAct(destructionActId)}
            type="button"
          >
            № {destructionActNumber}
          </button>
        )
      },
    })
  }

  if (tab === "annulled" || tab === "destroyed") {
    columns.push({
      accessorKey: "statusReason",
      header: "Примітка",
      meta: { className: tab === "destroyed" ? "w-48" : w.note, truncate: true },
      enableSorting: false,
      cell: ({ row }) =>
        row.original.statusReason ? (
          row.original.statusReason
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    })
  }

  if (tab === "annulled") {
    columns.push({
      accessorKey: "status",
      header: "Статус",
      meta: { className: w.status },
      cell: ({ row }) => <Status>{blankStatusLabels[row.original.status]}</Status>,
    })
  }

  return columns
}
